import { Injectable } from '@angular/core'
import { PlatformService } from '../platforms/platform.service'
import { MonitoringData, MonitoringMemoryData, MonitoringStorageData, SystemData } from '../interfaces/monitoring'
import { PowerState } from '../interfaces/power-state'
import { BehaviorSubject } from 'rxjs'
import { LogService } from 'htv-lib'

@Injectable({
  providedIn: 'root',
})
export class MonitoringService {
  private systemData!: SystemData
  private platformData?: object

  powerState = new BehaviorSubject<PowerState | undefined>(undefined)

  cpuUsage?: number
  memory?: MonitoringMemoryData
  storage?: MonitoringStorageData[]
  temperature?: number

  refreshTimer: any
  readonly refreshTimerDelayInMs = 60000

  constructor(
    private logService: LogService,
    private platformService: PlatformService
  ) {
    // do nothing
  }

  async init(): Promise<void> {
    this.logService.info(`[MonitoringService] Init`)

    this.initPowerState()
    this.systemData = await this.platformService.getSystemData()
    await this.refreshData()
    this.registerRefreshTimer()
  }

  getSystemData(): SystemData {
    return this.systemData
  }

  getMonitoringData(): MonitoringData {
    return {
      cpuUsage: this.cpuUsage ?? 0,
      memory: this.memory ?? {
        availableMemory: 0,
        totalMemory: 0,
      },
      storage: this.storage ?? [
        {
          type: '',
          availableCapacity: 0,
          capacity: 0,
          isRemovable: false,
        },
      ],
      temperature: 0,
    }
  }

  getPlatformData(): object {
    return this.platformData ?? {}
  }

  registerRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
    }
    this.refreshTimer = setInterval(() => {
      this.refreshData()
    }, this.refreshTimerDelayInMs)
  }

  async refreshData(): Promise<void> {
    this.logService.debug(`[MonitoringService] Refreshing monitoring data...`)

    const promises = [
      // CPU
      this.platformService.getCpuUsage().then(cpuUsage => {
        this.cpuUsage = cpuUsage
      }),

      // Memory
      this.platformService.getMemoryData().then(memory => {
        this.memory = memory
      }),

      // Storage
      this.platformService.getStorageData().then(storage => {
        this.storage = storage
      }),

      // Temperature
      this.platformService.getTemperature().then(temperature => {
        this.temperature = temperature
      }),

      // Platform specific data
      this.platformService.getPlatformData().then(platformData => {
        this.platformData = platformData
      }),
    ]

    try {
      await Promise.allSettled(promises)
    } catch {
      this.logService.error(`An error occurred while retrieving monitoring data`)
    }
  }

  async initPowerState(): Promise<void> {
    const powerState = await this.platformService.getPowerState()
    this.powerState.next(powerState)
    this.platformService.registerPowerStateListener(this.powerState)
  }
}
